import { useEffect, useRef, useState } from "react";
import { Bot, Send, X, MessageSquareText } from "lucide-react";
import { API_BASE } from "@/lib/api";

const SESSION_KEY = "pcv_chat_sid";

function ensureSession() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm the Provident Winworth virtual concierge. How can I help — pricing, floor plans, home loans, or NRI investment queries?" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: ensureSession(), message: q }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          const line = p.replace(/^data:\s*/, "").trim();
          if (!line) continue;
          if (line === "[DONE]") continue;
          try {
            const obj = JSON.parse(line);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + obj.delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Our concierge is momentarily unavailable. Please share your phone via the form and we'll call you back." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-toggle"
          onClick={() => setOpen(true)}
          className="hidden md:flex fixed bottom-[10.5rem] right-6 z-40 items-center gap-2 px-4 py-3 rounded-full glass-strong border border-[color:var(--pcv-gold)]/50 hover:border-[color:var(--pcv-gold)] text-[color:var(--pcv-gold)]"
          aria-label="Open concierge chat"
        >
          <MessageSquareText className="w-4 h-4" />
          <span className="text-xs uppercase tracking-[0.16em]">Ask AI Concierge</span>
        </button>
      )}
      {open && (
        <div
          data-testid="chat-panel"
          className="hidden md:flex fixed bottom-6 right-6 z-40 w-[380px] h-[520px] flex-col glass-strong border border-[color:var(--pcv-border)] shadow-2xl"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--pcv-border)]">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center bg-[color:var(--pcv-gold)] text-[color:var(--pcv-bg)]"><Bot className="w-4 h-4" /></div>
              <div>
                <div className="text-sm font-medium text-[color:var(--pcv-text)]">Purva Concierge</div>
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--pcv-text-2)]">Online · AI-Assisted</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close" aria-label="Close chat" className="p-1 text-[color:var(--pcv-text-2)] hover:text-[color:var(--pcv-gold)]"><X className="w-4 h-4" /></button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar px-4 py-3 space-y-3 text-sm">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 leading-relaxed ${
                  m.role === "user"
                    ? "bg-[color:var(--pcv-gold)] text-[color:var(--pcv-bg)]"
                    : "bg-[color:var(--pcv-surface-2)] text-[color:var(--pcv-text)] border border-[color:var(--pcv-border)]"
                }`}>{m.text || "…"}</div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-[color:var(--pcv-border)] flex gap-2">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about pricing, floor plans, NRI…"
              className="flex-1 bg-transparent border border-[color:var(--pcv-border)] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--pcv-gold)] text-[color:var(--pcv-text)]"
            />
            <button onClick={send} data-testid="chat-send" disabled={busy} className="btn-gold px-3 py-2 text-[10px]"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </>
  );
}
